import { APIKit } from "config/axios/index";
import store from "config/redux/index";
import { start, end, setDialogError } from "config/redux/slice";

import { ServerConstants, ServiceConstants } from "constant";
import { ServiceModel } from "models";
import { CreditRequestAttachmentResponse, CreditRequestPaymentRequest, CreditStatus, ListFilterRequest } from "types";

import { AppUtils } from "utils";

import { handleErrorWithTokenRenewal } from "./common";


const {
  API_V1_CREDIT_REQUESTS, API_V1_CREDIT_ATTACHMENTS, API_V1_CREDIT_ATTACHMENT_TYPES, API_V1_CREDIT_PAYMENTS, API_V1_CREDIT_LINKS
} = ServerConstants;
const {
  REQUEST_CREDIT_LIST_ID, REQUEST_CREDIT_VIEW_ID, REQUEST_CREDIT_PREVIOUS_LAST_ID, REQUEST_CREDIT_PREVIOUS_VIEW_ID, REQUEST_CREDIT_NEW_ID, REQUEST_CREDIT_OPTIONS_ID, REQUEST_CREDIT_STATUS_CHANGE_ID, REQUEST_CREDIT_DOWNLOAD_ID,
  REQUEST_CREDIT_POSTPONE_INSTALLMENT_ID, REQUEST_CREDIT_UPDATE_ID, REQUEST_CREDIT_UPDATE_PROPERTY_ID, REQUEST_CREDIT_UPDATE_MULTIPLE_PROPERTIES_ID, REQUEST_CREDIT_REQUEST_ID, REQUEST_CREDIT_LOCK_ID, REQUEST_CREDIT_UNLOCK_ID,
  REQUEST_CREDIT_AVAILABLE_STATUSES_ID,
  REQUEST_CREDIT_ATTACHMENT_LIST_ID, REQUEST_CREDIT_ATTACHMENT_UPDATE_ID, REQUEST_CREDIT_ATTACHMENT_UPLOAD_ID, REQUEST_CREDIT_ATTACHMENT_DELETE_ID,
  REQUEST_CREDIT_ATTACHMENT_TYPE_NEW_ID, REQUEST_CREDIT_ATTACHMENT_TYPE_DELIVERED_ID,
  REQUEST_CREDIT_PAYMENT_CREATE_ID, REQUEST_CREDIT_PAYMENT_DELETE_ID,
  REQUEST_CREDIT_LINK_CREATE_ID
} = ServiceConstants;

export async function creditsAll(filter: ListFilterRequest) {
  const requestId = REQUEST_CREDIT_LIST_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_CREDIT_REQUESTS}?page=${filter.page}&search=${filter.search}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditsAll, [filter], requestId);
  }
}

async function fetchCreditById(id: number, requestId: string, retryFn: (id: number) => Promise<void>) {
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_CREDIT_REQUESTS}/${id}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, retryFn, [id], requestId);
  }
}

export async function creditsOne(id: number) {
  await fetchCreditById(id, REQUEST_CREDIT_VIEW_ID, creditsOne);
}

export async function creditPreviousRequests(id: string, requestId: string, config?: { page?: number, pageSize?: number, search?: string, onlyActive?: boolean }) {
  try {
    store.dispatch(start(requestId));
    const params: { userId: string, page?: number, pageSize?: number, search?: string, onlyActive?: boolean } = { userId: id }
    if (config) {
      params.page = config.page;
      params.pageSize = config.pageSize;
      params.search = config.search;
      params.onlyActive = config.onlyActive;
    }
    const response = await APIKit.get(`${API_V1_CREDIT_REQUESTS}/previous`, { params });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditPreviousRequests, [id, requestId, config], requestId);
  }
}

export async function creditsPreviousLast(userId: number | string) {
  const requestId = REQUEST_CREDIT_PREVIOUS_LAST_ID;

  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_CREDIT_REQUESTS}/previous/last`, {
      params: { userId },
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    const status = err?.status ?? err?.response?.status;

    // Missing previous credit is a valid state for comparison fallback.
    if (status === 404) {
      store.dispatch(end(new ServiceModel(requestId)));
      return;
    }

    handleErrorWithTokenRenewal(err, creditsPreviousLast, [userId], requestId);
  }
}

export async function creditsPreviousOne(id: number) {
  await fetchCreditById(id, REQUEST_CREDIT_PREVIOUS_VIEW_ID, creditsPreviousOne);
}

export async function creditsNew(userId: number) {
  const requestId = REQUEST_CREDIT_NEW_ID;
  try {
    store.dispatch(start(requestId));
    const body = {
      userId: userId
    }
    const response = await APIKit.post(`${API_V1_CREDIT_REQUESTS}`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditsNew, [userId], requestId);
  }
}

export async function creditOptions() {
  const requestId = REQUEST_CREDIT_OPTIONS_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_CREDIT_REQUESTS}/options`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditOptions, [], requestId);
  }
}

export async function updateCreditRequest(id: number, data: any) {
  const requestId = REQUEST_CREDIT_UPDATE_ID;
  try {
    store.dispatch(start(requestId));
    const body = data;
    delete body['user'];
    Object.keys(body).forEach(key => {
      const val = body[key];
      if (val) {
        body[key] = AppUtils.convertToAppropriateValue(val);
      }
    })
    const response = await APIKit.put(`${API_V1_CREDIT_REQUESTS}/${id}`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditsUpdateProperty, [id, data], requestId);
  }
}

export async function creditsUpdateProperty(id: number, propertyName: string, propertyValue: any) {
  const requestId = REQUEST_CREDIT_UPDATE_PROPERTY_ID + id + propertyName;
  try {
    store.dispatch(start(requestId));
    const value = AppUtils.convertToAppropriateValue(propertyValue);
    const body = {
      name: propertyName,
      value: value
    };
    const response = await APIKit.patch(`${API_V1_CREDIT_REQUESTS}/${id}`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditsUpdateProperty, [id, propertyName, propertyValue], requestId);
  }
}

export async function creditsUpdateMultipleProperties(id: number, data: any) {
  const requestId = REQUEST_CREDIT_UPDATE_MULTIPLE_PROPERTIES_ID;
  try {
    store.dispatch(start(requestId));
    const body: { name: string, value: any }[] = [];
    Object.keys(data).forEach((el) => {
      body.push({ name: el, value: AppUtils.convertToAppropriateValue(data[el as any]) });
    });
    const response = await APIKit.patch(`${API_V1_CREDIT_REQUESTS}/${id}/multiple`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditsUpdateMultipleProperties, [id, data], requestId);
  }
}

export async function creditRequestRequest(id: number) {
  const requestId = REQUEST_CREDIT_REQUEST_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(`${API_V1_CREDIT_REQUESTS}/${id}/request`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditRequestRequest, [id], requestId);
  }
}

export async function creditStatusChange(id: number, status: CreditStatus) {
  const requestId = REQUEST_CREDIT_STATUS_CHANGE_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.patch(`${API_V1_CREDIT_REQUESTS}/${id}/status/${status}`);
    const data = response.data as any;
    if (data?.code === 'TODO_CREATED') {
      store.dispatch(end(new ServiceModel(requestId)));
      store.dispatch(setDialogError({ data, status: 200 }));
      return;
    }
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditStatusChange, [id, status], requestId);
  }
}

export async function creditAvailableStatuses(id: number) {
  const requestId = REQUEST_CREDIT_AVAILABLE_STATUSES_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_CREDIT_REQUESTS}/${id}/available-statuses`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditAvailableStatuses, [id], requestId);
  }
}

export async function creditDownload(id: number) {
  const requestId = REQUEST_CREDIT_DOWNLOAD_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_CREDIT_REQUESTS}/${id}/download`, {
      responseType: 'blob'
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditDownload, [id], requestId);
  }
}

export async function creditLock(id: number) {
  const requestId = REQUEST_CREDIT_LOCK_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(`${API_V1_CREDIT_REQUESTS}/${id}/lock`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditLock, [id], requestId);
  }
}

export async function creditUnlock(id: number) {
  const requestId = REQUEST_CREDIT_UNLOCK_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(`${API_V1_CREDIT_REQUESTS}/${id}/unlock`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditLock, [id], requestId);
  }
}

export async function creditPostponeInstalment(id: number) {
  const requestId = REQUEST_CREDIT_POSTPONE_INSTALLMENT_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(`${API_V1_CREDIT_REQUESTS}/${id}/postpone-instalment`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditLock, [id], requestId);
  }
}

export async function creditAttachments(id: number) {
  const requestId = REQUEST_CREDIT_ATTACHMENT_LIST_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_CREDIT_ATTACHMENTS}?rid=${id}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditAttachments, [id], requestId);
  }
}

export async function creditAttachmentUpdate(item: CreditRequestAttachmentResponse) {
  const requestId = REQUEST_CREDIT_ATTACHMENT_UPDATE_ID + item.id;
  try {
    store.dispatch(start(requestId));
    const body = {
      name: item.name
    };
    const response = await APIKit.patch(`${API_V1_CREDIT_ATTACHMENTS}/${item.id}`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditAttachmentUpdate, [item], requestId);
  }
}

export async function creditAttachmentUpload(id: number, files: any) {
  const requestId = REQUEST_CREDIT_ATTACHMENT_UPLOAD_ID;
  try {
    store.dispatch(start(requestId));
    const formData = new FormData();
    formData.append('rid', id.toString());
    files.forEach((file: any) => {
      formData.append("files", file);
    });
    const response = await APIKit.post(`${API_V1_CREDIT_ATTACHMENTS}/multiple`, formData, {
      headers: {
        ...APIKit.defaults.headers['common'],
        'Accept-Language': APIKit.defaults.headers['Accept-Language'],
        'Content-Type': 'multipart/form-data'
      }
    });
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditAttachmentUpload, [id, files], requestId);
  }
}

export async function creditAttachmentDelete(id: string) {
  const requestId = REQUEST_CREDIT_ATTACHMENT_DELETE_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.delete(`${API_V1_CREDIT_ATTACHMENTS}/${id}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditAttachmentDelete, [id], requestId);
  }
}

export async function creditAttachmentTypeCreate(creditId: number, name: string) {
  const requestId = REQUEST_CREDIT_ATTACHMENT_TYPE_NEW_ID;
  try {
    store.dispatch(start(requestId));
    const body = {
      requestId: creditId,
      name: name
    };
    const response = await APIKit.post(`${API_V1_CREDIT_ATTACHMENT_TYPES}`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditAttachmentTypeCreate, [creditId, name], requestId);
  }
}

export async function creditAttachmentTypeDelivered(id: number) {
  const requestId = REQUEST_CREDIT_ATTACHMENT_TYPE_DELIVERED_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.patch(`${API_V1_CREDIT_ATTACHMENT_TYPES}/${id}/delivered`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditAttachmentTypeDelivered, [id], requestId);
  }
}

export async function creditPaymentCreate(data: CreditRequestPaymentRequest) {
  const requestId = REQUEST_CREDIT_PAYMENT_CREATE_ID;
  try {
    store.dispatch(start(requestId));
    const body = {
      requestId: data.requestId,
      type: data.type,
      bank: data.bank,
      amount: data.amount
    };
    const response = await APIKit.post(`${API_V1_CREDIT_PAYMENTS}`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditPaymentCreate, [data], requestId);
  }
}

export async function creditPaymentDelete(id: number) {
  const requestId = REQUEST_CREDIT_PAYMENT_DELETE_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.delete(`${API_V1_CREDIT_PAYMENTS}/${id}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditPaymentDelete, [id], requestId);
  }
}

export async function creditLinkCreate(objectId: number) {
  const requestId = REQUEST_CREDIT_LINK_CREATE_ID;
  try {
    store.dispatch(start(requestId));
    const body = {
      objectId: objectId
    };
    const response = await APIKit.post(`${API_V1_CREDIT_LINKS}`, body);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, creditLinkCreate, [objectId], requestId);
  }
}
